import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Button, Input, Tag, message as antMessage } from "antd";
import { ChatCircleDots, ImageSquare, PaperPlaneRight, X } from "@phosphor-icons/react";

import {
  applyBriefRevision,
  dismissBriefRevision,
  listBriefChat,
  sendBriefChat,
} from "../../service/analysis";
import BriefingMarkdown from "./BriefingMarkdown";

/**
 * BriefChatPanel — the Brief-scoped correction chat (2026-07-23).
 *
 * A conversation anchored to one AnalysisJob: the attorney discusses
 * The Brief with the model, attaches screenshots (paste or picker), and
 * asks for corrections. When the model proposes one, it arrives as a
 * revision card with Apply / Dismiss; applying writes the correction
 * into the live Brief and the parent re-fetches the report so the
 * change shows instantly. Every exchange is recorded server-side for
 * the training corpus.
 *
 * Visibility is decided by the PARENT (`AnalysisReport`) via the same
 * rollout-gate probe as the feedback bars.
 */

const MAX_IMAGES = 4;

const PanelShell = styled.div`
  margin-top: 16px;
  border: 1px solid #dbe2ea;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
`;

const PanelHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: #f5f8fc;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
`;

const MessageList = styled.div`
  max-height: 380px;
  overflow-y: auto;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Bubble = styled.div`
  max-width: 88%;
  padding: 8px 12px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.5;
  align-self: ${(props) => (props.$role === "user" ? "flex-end" : "flex-start")};
  background: ${(props) => (props.$role === "user" ? "#e8f0fe" : "#f3f4f6")};
  color: #1f2937;
  white-space: pre-wrap;
  overflow-wrap: anywhere;

  p:last-child {
    margin-bottom: 0;
  }
`;

const Thumb = styled.img`
  max-width: 160px;
  max-height: 120px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  display: block;
  margin-top: 6px;
`;

const RevisionCard = styled.div`
  align-self: stretch;
  border: 1px solid #c7d7f0;
  border-left: 4px solid #2563eb;
  border-radius: 8px;
  background: #f8fbff;
  padding: 10px 12px;
  font-size: 13px;
`;

const RevisionPreview = styled.div`
  max-height: ${(props) => (props.$expanded ? "none" : "180px")};
  overflow: hidden;
  position: relative;
  margin: 8px 0;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
`;

const InputRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
  padding: 10px 14px;
  border-top: 1px solid #e5e7eb;
`;

const PendingImages = styled.div`
  display: flex;
  gap: 8px;
  padding: 8px 14px 0;
  flex-wrap: wrap;
`;

const PendingImage = styled.div`
  position: relative;

  img {
    max-width: 90px;
    max-height: 70px;
    border-radius: 6px;
    border: 1px solid #d1d5db;
    display: block;
  }

  button {
    position: absolute;
    top: -6px;
    right: -6px;
    border: none;
    border-radius: 50%;
    width: 18px;
    height: 18px;
    background: #374151;
    color: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }
`;

const Hint = styled.div`
  font-size: 11px;
  color: #9ca3af;
  padding: 0 14px 10px;
`;

const readFileAsImagePayload = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      const match = dataUrl.match(/^data:([^;]+);base64,(.*)$/s);
      if (!match) {
        reject(new Error("Unsupported image encoding"));
        return;
      }
      resolve({ mime_type: match[1], data_base64: match[2] });
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const revisionTargetLabel = (revision) =>
  revision.target_kind === "strategic_overview"
    ? "Strategic Overview"
    : revision.condition_name || "Condition";

const BriefChatPanel = ({ jobId, onBriefUpdated }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [pendingImages, setPendingImages] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [expandedRevisions, setExpandedRevisions] = useState({});
  const [actingRevisionId, setActingRevisionId] = useState(null);
  const listRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    Promise.resolve(listBriefChat(jobId))
      .then((response) => {
        if (cancelled || !response || response.status !== 200) return;
        setMessages(response.data?.results || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, open]);

  const addFiles = async (files) => {
    const incoming = Array.from(files || []).filter((f) =>
      f.type.startsWith("image/")
    );
    if (!incoming.length) return;
    try {
      const payloads = await Promise.all(incoming.map(readFileAsImagePayload));
      // Enforce the cap inside the functional update — two quick pastes
      // each see a stale length otherwise and together overshoot.
      setPendingImages((prev) => {
        const combined = [...prev, ...payloads];
        if (combined.length > MAX_IMAGES) {
          antMessage.warning(`At most ${MAX_IMAGES} images per message.`);
        }
        return combined.slice(0, MAX_IMAGES);
      });
    } catch (e) {
      antMessage.error("Could not read that image.");
    }
  };

  const handlePaste = (event) => {
    const files = Array.from(event.clipboardData?.files || []);
    if (files.length) {
      event.preventDefault();
      addFiles(files);
    }
  };

  const handleSend = async () => {
    const text = draft.trim();
    if ((!text && !pendingImages.length) || isSending) return;
    setIsSending(true);
    try {
      const response = await sendBriefChat(jobId, {
        message: text,
        images: pendingImages,
      });
      const { user_message: userMsg, assistant_message: assistantMsg } =
        response.data || {};
      setMessages((prev) => [
        ...prev,
        ...(userMsg ? [userMsg] : []),
        ...(assistantMsg ? [assistantMsg] : []),
      ]);
      setDraft("");
      setPendingImages([]);
    } catch (e) {
      antMessage.error("The assistant could not respond. Try again.");
    } finally {
      setIsSending(false);
    }
  };

  const patchRevision = (updated) => {
    setMessages((prev) =>
      prev.map((msg) => ({
        ...msg,
        revisions: (msg.revisions || []).map((rev) =>
          rev.id === updated.id ? { ...rev, ...updated } : rev
        ),
      }))
    );
  };

  const handleApply = async (revision) => {
    // One revision action at a time — a fast double-click fires before
    // React re-renders the disabled state.
    if (actingRevisionId != null) return;
    setActingRevisionId(revision.id);
    try {
      const response = await applyBriefRevision(revision.id);
      patchRevision(response.data);
      antMessage.success(`Applied to ${revisionTargetLabel(revision)}.`);
      if (onBriefUpdated) onBriefUpdated();
    } catch (e) {
      antMessage.error("Could not apply the revision.");
    } finally {
      setActingRevisionId(null);
    }
  };

  const handleDismiss = async (revision) => {
    if (actingRevisionId != null) return;
    setActingRevisionId(revision.id);
    try {
      const response = await dismissBriefRevision(revision.id);
      patchRevision(response.data);
    } catch (e) {
      antMessage.error("Could not dismiss the revision.");
    } finally {
      setActingRevisionId(null);
    }
  };

  const renderRevision = (revision) => {
    const expanded = !!expandedRevisions[revision.id];
    return (
      <RevisionCard key={`rev-${revision.id}`} data-testid="brief-revision-card">
        <div>
          <strong>Proposed update — {revisionTargetLabel(revision)}</strong>{" "}
          {revision.status !== "proposed" && (
            <Tag color={revision.status === "applied" ? "green" : "default"}>
              {revision.status}
            </Tag>
          )}
        </div>
        <RevisionPreview $expanded={expanded}>
          <BriefingMarkdown content={revision.preview_text || ""} />
        </RevisionPreview>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {revision.status === "proposed" && (
            <>
              <Button
                size="small"
                type="primary"
                loading={actingRevisionId === revision.id}
                onClick={() => handleApply(revision)}
              >
                Apply to Brief
              </Button>
              <Button
                size="small"
                disabled={actingRevisionId === revision.id}
                onClick={() => handleDismiss(revision)}
              >
                Dismiss
              </Button>
            </>
          )}
          <Button
            size="small"
            type="link"
            onClick={() =>
              setExpandedRevisions((prev) => ({
                ...prev,
                [revision.id]: !expanded,
              }))
            }
          >
            {expanded ? "Collapse" : "Show full text"}
          </Button>
        </div>
      </RevisionCard>
    );
  };

  return (
    <PanelShell data-testid="brief-chat-panel">
      <PanelHeader type="button" onClick={() => setOpen((prev) => !prev)}>
        <ChatCircleDots size={16} weight="bold" />
        Discuss with AI — corrections land in the Brief
        <span style={{ marginLeft: "auto", color: "#6b7280", fontWeight: 400 }}>
          {open ? "Hide" : "Open"}
        </span>
      </PanelHeader>

      {open && (
        <>
          <MessageList ref={listRef} data-testid="brief-chat-messages">
            {messages.length === 0 && (
              <Bubble $role="assistant">
                Ask about this Brief, or tell me what to correct — you can
                paste screenshots. When I propose a fix you can apply it to
                the Brief with one click.
              </Bubble>
            )}
            {messages.map((msg) => (
              <React.Fragment key={msg.id}>
                <Bubble $role={msg.role}>
                  {msg.role === "assistant" ? (
                    <BriefingMarkdown content={msg.content || ""} />
                  ) : (
                    msg.content
                  )}
                  {(msg.images || []).map((img) => (
                    <Thumb key={img.id} src={img.data_uri} alt="attachment" />
                  ))}
                </Bubble>
                {(msg.revisions || []).map(renderRevision)}
              </React.Fragment>
            ))}
            {isSending && <Bubble $role="assistant">Thinking…</Bubble>}
          </MessageList>

          {pendingImages.length > 0 && (
            <PendingImages>
              {pendingImages.map((img, idx) => (
                <PendingImage key={idx}>
                  <img
                    src={`data:${img.mime_type};base64,${img.data_base64}`}
                    alt="pending attachment"
                  />
                  <button
                    type="button"
                    aria-label="Remove image"
                    onClick={() =>
                      setPendingImages((prev) =>
                        prev.filter((_, i) => i !== idx)
                      )
                    }
                  >
                    <X size={10} />
                  </button>
                </PendingImage>
              ))}
            </PendingImages>
          )}

          <InputRow>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              multiple
              style={{ display: "none" }}
              onChange={(event) => {
                addFiles(event.target.files);
                event.target.value = "";
              }}
            />
            <Button
              icon={<ImageSquare size={16} />}
              onClick={() => fileInputRef.current?.click()}
              aria-label="Attach image"
            />
            <Input.TextArea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onPaste={handlePaste}
              onPressEnter={(event) => {
                if (!event.shiftKey) {
                  event.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Ask a question or request a correction…"
              autoSize={{ minRows: 1, maxRows: 5 }}
              disabled={isSending}
              data-testid="brief-chat-input"
            />
            <Button
              type="primary"
              icon={<PaperPlaneRight size={16} />}
              onClick={handleSend}
              loading={isSending}
              disabled={!draft.trim() && !pendingImages.length}
              aria-label="Send"
            />
          </InputRow>
          <Hint>
            Enter to send · Shift+Enter for a new line · paste or attach up to{" "}
            {MAX_IMAGES} screenshots
          </Hint>
        </>
      )}
    </PanelShell>
  );
};

BriefChatPanel.propTypes = {
  jobId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onBriefUpdated: PropTypes.func,
};

export default BriefChatPanel;
